import type { PaymentMethodsState } from "../Types/paymentsPaymentTypes";
import { isBankAccount, isCreditCard } from "./paymentsMakePaymentsUtils";

/**
 * The per-method copy of the Make Payments flow. Bank accounts and credit
 * cards are charged by us on confirm; cheque/e-transfer/EFT/wire are sent by
 * the customer, so those need sending instructions and a reference field.
 */

function isDirectCharge(
  state: PaymentMethodsState,
  selectedPaymentMethod: string | undefined,
): boolean {
  return (
    isBankAccount(state, selectedPaymentMethod) ||
    isCreditCard(state, selectedPaymentMethod)
  );
}

export function paymentsConfirmButtonText(
  state: PaymentMethodsState,
  selectedPaymentMethod: string | undefined,
): string {
  if (isBankAccount(state, selectedPaymentMethod)) {
    return "Process Bank Payment";
  }
  if (isCreditCard(state, selectedPaymentMethod)) {
    return "Process Card Payment";
  }
  switch (selectedPaymentMethod) {
    case "cheque":
      return "Confirm Cheque Sent";
    case "etransfer":
      return "Confirm eTransfer Sent";
    case "eft":
      return "Confirm EFT/ACH Sent";
    case "wire":
      return "Confirm Wire Sent";
    case undefined:
    default:
      return "Confirm";
  }
}

/** How to actually send the money, for the send-it-yourself methods. */
export function paymentsMethodInstructions(
  state: PaymentMethodsState,
  selectedPaymentMethod: string | undefined,
): string | undefined {
  if (isDirectCharge(state, selectedPaymentMethod)) {
    return undefined;
  }
  switch (selectedPaymentMethod) {
    case "cheque":
      return "Please check with our team for the correct address to mail it to.";
    case "etransfer":
      return "Please send eTransfer to billing@freightsimple.com";
    case "eft":
      return "Please check with our team for the correct EFT/ACH instructions.";
    case "wire":
      return "Please check with our team for the correct wire instructions.";
    case undefined:
    default:
      return undefined;
  }
}

/** What confirming means for the money — charged now, or acknowledged as sent. */
export function paymentsChargeTimingText(
  state: PaymentMethodsState,
  selectedPaymentMethod: string | undefined,
): string {
  if (isDirectCharge(state, selectedPaymentMethod)) {
    return "You will be charged immediately after you confirm this payment.";
  }
  return "By confirming this payment, you acknowledge that the payment has already been sent.";
}

/** The reference field only makes sense for payments the customer sends. */
export function paymentsNeedsReference(
  state: PaymentMethodsState,
  selectedPaymentMethod: string | undefined,
): boolean {
  return !isDirectCharge(state, selectedPaymentMethod);
}
